import { useCallback } from 'react';
import { dictionaries } from './dictionaries';
import { useLocaleSafe } from './context';
import { usePlatformMetadata } from '../queries';
import type { LocaleDict } from './types';
import type { PlatformMetadata } from '../types';

/** Surfaces the HOST owns and names itself: its own transport, the terminal, API tokens, internal work
 *  and delegated runs. Their curated translations outrank any installed plugin that happens to declare
 *  the same id. `cron` is deliberately absent — plan 05's classification cut moved scheduled work to the
 *  cronjob plugin, so its live name comes from that plugin's metadata, never from a core cron label. */
const CORE_SURFACES = new Set(['api', 'subagent', 'web', 'cli', 'internal']);

/** Name a surface: the host's own translation, else the installed platform's localized metadata label,
 *  else the recorded id. The last fallback is what keeps a historical row (uninstalled plugin, or a
 *  metadata read that is pending or failed) visible as the id it recorded instead of an invented name. */
export function surfaceLabel(t: LocaleDict, surface: string, metadata?: readonly PlatformMetadata[]): string {
  const labels = t.dashboard.surfaces;
  if (CORE_SURFACES.has(surface) && Object.hasOwn(labels, surface)) {
    return labels[surface as keyof typeof labels];
  }
  const contributed = metadata?.find((entry) => entry.platform === surface)?.label;
  return contributed || surface;
}

/** The same lookup bound to the one locale-keyed metadata projection the host already caches, so every
 *  surface on a page (icon titles, dashboard rows, conversation filters) resolves to the same name. The
 *  query's own loading and failure states are deliberately not handled here: absent metadata is the raw
 *  recorded id, which is exactly what an unavailable or still-loading platform must show. */
export function useSurfaceLabel(): (surface: string) => string {
  const locale = useLocaleSafe();
  const { data } = usePlatformMetadata(locale);
  const t = dictionaries[locale];
  return useCallback((surface: string) => surfaceLabel(t, surface, data), [t, data]);
}
